import {gql} from '@apollo/client';
import React, {useState} from 'react';
import {v4 as uuid4} from 'uuid';

import {assert} from 'Asserts';
import * as BenchlingUtil from 'BenchlingUtil';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {MAX_GRID_TITLE_NAME_LENGTH} from 'notebook/canvas/CanvasConstants';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  createRequestV2EventMetadata,
  REQUESTS_V2_CREATE_REQUEST_V2_DEFINITION_STARTED_EVENT,
  REQUESTS_V2_CREATE_REQUEST_V2_DEFINITION_ENDED_EVENT,
} from 'performance/processRequestsV2Events';
import {CreateRequestV2TemplateModalQuery} from 'processes/requests_v2/modals/__generated/CreateRequestV2TemplateModalQuery';
import {
  createRequestV2Definition,
  requestV2DefinitionPageUrl,
} from 'processes/requests_v2/network/RequestV2DefinitionDAO';
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_REQUEST_DEFINITION_PREFIX_LENGTH,
  maybeStrLengthValidationMessage,
  RequestTemplateInputGroup,
  RequestV2DefinitionDescriptionField,
  RequestV2DefinitionPrefixField,
} from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionUtils';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import ErrorMessage from 'ui-library/ErrorMessage';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import {omit} from 'util/omitKey';
import {BasicEvent} from 'util/Types';

import {TemplateCollectionGraphQL} from '../../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionGraphQL';

export const CREATE_REQUEST_V2_TEMPLATE_MODAL_QUERY = gql`
  query CreateRequestV2TemplateModalQuery @skipAudit @engComponent(name: WORKFLOWS) {
    processTaskSchemasSupportingPlatformFeature(platformFeature: CAN_CREATE_REQUEST_V2) {
      id
      name
      canOnlyBePartOfFlowchart
      archiveRecord {
        purpose
      }
    }
  }
`;

interface ProcessTaskSchema {
  id: string;
  name: string;
}

export interface CreateRequestV2TemplateModalWithoutFixedSchema {
  initialTaskSchema?: ProcessTaskSchema;
  taskSchemaCannotChange?: false;
}

export interface CreateRequestV2TemplateModalFromTaskSchema {
  initialTaskSchema: ProcessTaskSchema;
  taskSchemaCannotChange: true;
}

export interface CreateRequestV2TemplateModalCommonProps {
  onRequestClose: () => void;
  initialTemplateCollection?: TemplateCollectionGraphQL;
  actionOrigin: ActionOrigin;
}

export type CreateRequestV2TemplateModalProps = CreateRequestV2TemplateModalCommonProps &
  (CreateRequestV2TemplateModalWithoutFixedSchema | CreateRequestV2TemplateModalFromTaskSchema);

export default function CreateRequestV2TemplateModal(props: CreateRequestV2TemplateModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [prefix, setPrefix] = useState('');
  const [templateCollection, setTemplateCollection] = useState(props.initialTemplateCollection);
  const [taskSchema, setTaskSchema] = useState(props.initialTaskSchema);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function manageCollections(e: BasicEvent): void {
    const url = TemplateCollectionDAO.collectionsURL();
    BenchlingUtil.navigate(e, url);
    props.onRequestClose();
  }

  function isSubmitEnabled(): boolean {
    const hasTemplateCollection = templateCollection != null;
    const hasTaskSchema = taskSchema != null;

    return (
      name.trim().length > 0 &&
      maybeStrLengthValidationMessage(name, MAX_GRID_TITLE_NAME_LENGTH) == null &&
      hasTemplateCollection &&
      hasTaskSchema &&
      maybeStrLengthValidationMessage(description, MAX_DESCRIPTION_LENGTH) == null &&
      prefix.trim().length > 0 &&
      maybeStrLengthValidationMessage(prefix, MAX_REQUEST_DEFINITION_PREFIX_LENGTH) == null
    );
  }

  function checkLength(inputStr: string, propertyName: string, maxLength: number): void {
    const maybeErrorMessage = maybeStrLengthValidationMessage(inputStr, maxLength);

    if (maybeErrorMessage != null) {
      setErrors({
        ...errors,
        [propertyName]: maybeErrorMessage,
      });
    } else {
      setErrors({...omit(errors, propertyName)});
    }
  }

  const enableSubmit = isSubmitEnabled();

  return (
    <Modal
      title="Create request template"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={async () => {
        assert(
          taskSchema != null && templateCollection != null,
          'Submit button should be disabled if task schema or template collection is not specified'
        );
        setErrors({});
        const profileEventId = uuid4();
        const profileEventMetadata = createRequestV2EventMetadata(props.actionOrigin);
        logProfilerTimingEvent(
          {...REQUESTS_V2_CREATE_REQUEST_V2_DEFINITION_STARTED_EVENT, id: profileEventId},
          profileEventMetadata
        );
        try {
          const response = await createRequestV2Definition({
            taskSchemaId: taskSchema.id,
            name: name.trim(),
            templateCollectionId: templateCollection.id,
            prefix: prefix.trim(),
            description: description.length > 0 ? description.trim() : undefined,
            actionOrigin: props.actionOrigin,
            actionMethod: ActionMethod.CREATE_MODAL,
          });

          assert(response.createdIds.length === 1, 'Expected exactly one RequestV2Definition to be created');

          props.onRequestClose();

          logProfilerTimingEvent(
            {...REQUESTS_V2_CREATE_REQUEST_V2_DEFINITION_ENDED_EVENT, id: profileEventId},
            profileEventMetadata
          );

          BenchlingUtil.navigate(null, requestV2DefinitionPageUrl(response.createdIds[0]));

          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (e) {
          if (e.responseJSON?.errors != null) {
            setErrors(e.responseJSON.errors);
          } else {
            notifications.notifyError(e);
          }
        }
      }}
      buttons={[
        {
          text: 'Create',
          submit: true,
          disabled: !enableSubmit,
          tooltipTitle: enableSubmit ? null : 'Complete all required fields to create this request template',
        },
        {text: 'Manage Collections', onClick: manageCollections, asLink: true, pullLeft: true},
      ]}
      dataTestAttrs={{component: 'CreateRequestV2TemplateModal', element: 'modal'}}
    >
      <FormGroup dataTestAttrs={{component: 'CreateRequestV2TemplateModal', element: 'name'}}>
        <RequestTemplateInputGroup>
          <Label required={true}>Name</Label>
          <TextInput
            value={name}
            hasError={errors?.name != null}
            placeholder="Enter a name"
            onChange={(e) => {
              setName(e.target.value);
              checkLength(e.target.value, 'name', MAX_GRID_TITLE_NAME_LENGTH);
            }}
            autoFocus={true}
            dataTestAttrs={{
              component: 'CreateRequestV2TemplateModal',
              element: 'nameField',
            }}
          />
          <ErrorMessage
            error={errors?.name}
            dataTestAttrs={{
              component: 'CreateRequestV2TemplateModal',
              element: 'nameFieldErrorMessage',
            }}
          />
        </RequestTemplateInputGroup>
        <RequestV2DefinitionDescriptionField
          description={description}
          onChangeHandler={(e) => {
            setDescription(e.target.value);
            checkLength(e.target.value, 'description', MAX_DESCRIPTION_LENGTH);
          }}
          errorMessage={errors?.description}
          dataTestComponentName="CreateRequestV2TemplateModal"
        />
        <RequestV2DefinitionPrefixField
          prefix={prefix}
          onChangeHandler={(e) => {
            setPrefix(e.target.value);
            checkLength(e.target.value, 'prefix', MAX_REQUEST_DEFINITION_PREFIX_LENGTH);
          }}
          errorMessage={errors?.prefix}
          dataTestComponentName="CreateRequestV2TemplateModal"
        />
        <RequestTemplateInputGroup>
          <Label required={true}>Task Schema</Label>
          <TaskSchemaSelector
            value={taskSchema}
            disabled={props.taskSchemaCannotChange}
            onSelect={(schema: ProcessTaskSchema) => {
              setTaskSchema(schema);
            }}
          />
          <ErrorMessage
            error={errors?.task_schema}
            dataTestAttrs={{
              component: 'CreateRequestV2TemplateModal',
              element: 'taskSchemaFieldErrorMessage',
            }}
          />
        </RequestTemplateInputGroup>
        <RequestTemplateInputGroup>
          <Label required={true}>Template Collection</Label>
          <TemplateCollectionSelector
            value={templateCollection}
            onChange={(collection) => {
              setTemplateCollection(collection);
            }}
            searchable={true}
          />
        </RequestTemplateInputGroup>
      </FormGroup>
    </Modal>
  );
}

export function TaskSchemaSelector(props: {
  value?: ProcessTaskSchema;
  onSelect: (schema: ProcessTaskSchema) => void;
  disabled?: boolean;
}): JSX.Element | null {
  const schemaQueryResult = useBenchlingQuery<CreateRequestV2TemplateModalQuery>(
    CREATE_REQUEST_V2_TEMPLATE_MODAL_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );

  if (schemaQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (schemaQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load schemas');
    return null;
  }

  return (
    <SelectDropdown
      displayName={props.value?.name ?? ''}
      disabled={props.disabled}
      menu={
        <SearchableMenu
          width={Menu.WIDTHS.FULL}
          optionDatas={schemaQueryResult.data.processTaskSchemasSupportingPlatformFeature
            .filter((schema) => schema.archiveRecord == null && schema.canOnlyBePartOfFlowchart !== true)
            .map((schema) => ({
              name: schema.name,
              value: schema,
            }))}
          onSelect={props.onSelect}
        />
      }
      dataTestAttrs={{component: 'CreateRequestV2TemplateModal', element: 'taskSchemaSelector'}}
    />
  );
}
